import { i18n } from "@dehub/theme";
import { ReturnApi } from "../apis";
import type { IReturnListParams, IReturnSummary } from "../apis/return";

export interface IExportProgress {
  current: number;
  total: number;
}

// §25 — the CSV column registry. The export modal renders one checkbox per
// column (all on by default) and passes the chosen keys to exportReturnsCsv.
export type ReturnsCsvColumnKey =
  | "returnNumber" | "orderNumber" | "buyer" | "seller" | "reason"
  | "resolution" | "refundMethod" | "status" | "amount" | "currency" | "requestedAt";

const returnsCsvColumnDefs: { key: ReturnsCsvColumnKey; labelKey: string; value: (r: IReturnSummary) => unknown }[] = [
  { key: "returnNumber", labelKey: "order:returnsCsv.returnNumber", value: (r) => r.returnNumber },
  { key: "orderNumber",  labelKey: "order:returnsCsv.orderNumber",  value: (r) => r.orderNumber ?? "" },
  { key: "buyer",        labelKey: "order:returnsCsv.buyer",        value: (r) => r.buyerBusinessName ?? "" },
  { key: "seller",       labelKey: "order:returnsCsv.seller",       value: (r) => r.sellerBusinessName ?? "" },
  { key: "reason",       labelKey: "order:returnsCsv.reason",       value: (r) => r.returnReason },
  { key: "resolution",   labelKey: "order:returnsCsv.resolution",   value: (r) => r.resolutionType ?? "" },
  { key: "refundMethod", labelKey: "order:returnsCsv.refundMethod", value: (r) => r.refundMethod ?? "" },
  { key: "status",       labelKey: "order:returnsCsv.status",       value: (r) => r.status },
  { key: "amount",       labelKey: "order:returnsCsv.amount",       value: (r) => r.totalRequestedAmount },
  { key: "currency",     labelKey: "order:returnsCsv.currency",     value: (r) => r.currency },
  { key: "requestedAt",  labelKey: "order:returnsCsv.requestedAt",  value: (r) => r.requestedAt }
];

export const RETURNS_CSV_COLUMNS: { key: ReturnsCsvColumnKey; label: string; value: (r: IReturnSummary) => unknown }[] =
  returnsCsvColumnDefs.map((c) => ({
    key: c.key,
    value: c.value,
    get label() { return i18n.t(c.labelKey); },
  }));

export interface IExportCsvOptions {
  columns?: ReturnsCsvColumnKey[];
  onProgress?: (p: IExportProgress) => void;
}

export async function exportReturnsCsv(
  baseParams: IReturnListParams,
  opts?: IExportCsvOptions
): Promise<Blob> {
  const PAGE_SIZE = 100;
  const MAX_ROWS = 5000;
  const all: IReturnSummary[] = [];
  let page = 1;
  let totalPages = 1;

  do {
    const res = await ReturnApi.list({ ...baseParams, page, limit: PAGE_SIZE });
    all.push(...res.returns);
    totalPages = res.pagination.totalPages;
    opts?.onProgress?.({ current: all.length, total: res.pagination.total });
    if (all.length >= MAX_ROWS) break;
    page += 1;
  } while (page <= totalPages);

  // Selected columns, in the registry's canonical order; default = all.
  const chosen = opts?.columns?.length
    ? RETURNS_CSV_COLUMNS.filter((c) => opts.columns!.includes(c.key))
    : RETURNS_CSV_COLUMNS;

  const escape = (v: unknown): string => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const lines = [
    chosen.map((c) => escape(c.label)).join(","),
    ...all.map((r) => chosen.map((c) => escape(c.value(r))).join(","))
  ];

  // Prepend UTF-8 BOM so Excel detects Cyrillic correctly
  return new Blob(["﻿" + lines.join("\n")], {
    type: "text/csv;charset=utf-8"
  });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
